import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Activity, RefreshCw, Download, CheckCircle2, AlertTriangle, XCircle, Clock, Wifi, Terminal, Bug, Check, Loader2 } from 'lucide-react';
import { useHealthCheck, ConnectorHealthResult } from '../../hooks/useHealthCheck';
import { runTool } from '../../services/toolRunner';
import { useProjectContext } from '../../hooks/useProjectContext';
function StatusIcon({
  status,
  isChecking
}: {
  status: string;
  isChecking: boolean;
}) {
  if (isChecking) return <RefreshCw size={16} className="text-slate-400 animate-spin" data-id="element-4893" />;
  switch (status) {
    case 'connected':
      return <CheckCircle2 size={16} className="text-emerald-400" data-id="element-4894" />;
    case 'degraded':
      return <AlertTriangle size={16} className="text-amber-400" data-id="element-4895" />;
    case 'disconnected':
      return <XCircle size={16} className="text-red-400" data-id="element-4896" />;
    default:
      return <Activity size={16} className="text-slate-500" data-id="element-4897" />;
  }
}
function StatusBadge({
  status
}: {
  status: string;
}) {
  switch (status) {
    case 'connected':
      return <Badge variant="success" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20" data-id="element-4898">
          Conectado
        </Badge>;
    case 'degraded':
      return <Badge variant="warning" className="bg-amber-500/10 text-amber-400 border-amber-500/20" data-id="element-4899">
          Degradado
        </Badge>;
    case 'disconnected':
      return <Badge variant="destructive" className="bg-red-500/10 text-red-400 border-red-500/20" data-id="element-4900">
          Desconectado
        </Badge>;
    case 'checking':
      return <Badge variant="outline" className="text-slate-400 border-slate-700" data-id="element-4901">
          Verificando...
        </Badge>;
    default:
      return <Badge variant="outline" className="text-slate-500 border-slate-700" data-id="element-4902">
          Desconocido
        </Badge>;
  }
}
function formatTimeAgo(timestamp: number | null) {
  if (!timestamp) return 'Nunca';
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `Hace ${seconds} seg`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} hs`;
  return new Date(timestamp).toLocaleDateString();
}
export function HealthCheckPanel({
  onNavigate
}: {
  onNavigate?: (tab: string) => void;
}) {
  const {
    healthData,
    isCheckingAll,
    runCheck,
    runAllChecks,
    exportDiagnostics
  } = useHealthCheck();
  const {
    activeProject
  } = useProjectContext();
  // Bug reporting state: connectorId → 'idle' | 'sending' | 'sent' | 'error'
  const [reportStatus, setReportStatus] = useState<Record<string, 'idle' | 'sending' | 'sent' | 'error'>>({});
  // Toast-like message for report feedback
  const [reportMessage, setReportMessage] = useState<{
    connectorId: string;
    text: string;
    type: 'success' | 'error';
  } | null>(null);
  const showReportMessage = (connectorId: string, text: string, type: 'success' | 'error') => {
    setReportMessage({
      connectorId,
      text,
      type
    });
    setTimeout(() => setReportMessage(null), 5000);
  };
  const reportBugToJira = async (connector: ConnectorHealthResult) => {
    const projectKey = activeProject?.key;
    if (!projectKey) {
      showReportMessage(connector.connectorId, 'Seleccioná un proyecto primero (desde el selector arriba a la izquierda)', 'error');
      return;
    }
    setReportStatus(prev => ({
      ...prev,
      [connector.connectorId]: 'sending'
    }));
    const summary = `[Health Check] ${connector.name} — ${connector.status === 'disconnected' ? 'Desconectado' : 'Degradado'}`;
    const description = [`*Conector:* ${connector.name} (${connector.connectorId})`, `*Estado:* ${connector.status}`, `*Último error:* ${connector.lastError || 'Sin detalle'}`, `*Latencia:* ${connector.latency !== null ? connector.latency + 'ms' : 'N/A'}`, `*Token expira en:* ${connector.tokenExpiresIn || 'N/A'}`, `*Última verificación:* ${connector.lastChecked ? new Date(connector.lastChecked).toLocaleString('es-AR') : 'Nunca'}`, '', `_Reportado automáticamente desde Health Check — ${new Date().toLocaleString('es-AR')}_`].join('\n');
    try {
      const result = await runTool('create-bug', {
        project: projectKey,
        summary,
        description,
        priority: connector.status === 'disconnected' ? 'High' : 'Medium'
      });
      if (result.success) {
        const issueKey = result.data?.key || result.data?.id || '';
        setReportStatus(prev => ({
          ...prev,
          [connector.connectorId]: 'sent'
        }));
        showReportMessage(connector.connectorId, `Bug creado en Jira${issueKey ? `: ${issueKey}` : ''} ✓`, 'success');
        setTimeout(() => {
          setReportStatus(prev => ({
            ...prev,
            [connector.connectorId]: 'idle'
          }));
        }, 4000);
      } else {
        console.error('Failed to create bug:', result.error);
        setReportStatus(prev => ({
          ...prev,
          [connector.connectorId]: 'error'
        }));
        showReportMessage(connector.connectorId, `Error: ${result.error || 'No se pudo crear el bug. Verificá la conexión con Jira en Settings.'}`, 'error');
        setTimeout(() => {
          setReportStatus(prev => ({
            ...prev,
            [connector.connectorId]: 'idle'
          }));
        }, 5000);
      }
    } catch (err: any) {
      console.error('Error reporting bug:', err);
      setReportStatus(prev => ({
        ...prev,
        [connector.connectorId]: 'error'
      }));
      showReportMessage(connector.connectorId, `Error: ${err?.message || 'Error de conexión con Jira'}`, 'error');
      setTimeout(() => {
        setReportStatus(prev => ({
          ...prev,
          [connector.connectorId]: 'idle'
        }));
      }, 5000);
    }
  };
  // Run initial check if never checked
  useEffect(() => {
    const needsInitialCheck = healthData.some(c => c.status === 'unknown');
    if (needsInitialCheck && !isCheckingAll) {
      runAllChecks();
    }
  }, []);
  const connectedCount = healthData.filter(c => c.status === 'connected').length;
  const degradedCount = healthData.filter(c => c.status === 'degraded').length;
  const disconnectedCount = healthData.filter(c => c.status === 'disconnected').length;
  return <div className="h-full overflow-y-auto p-4 lg:p-8 animate-in fade-in duration-300 bg-slate-950 text-slate-200" data-id="element-4903">
      <div className="max-w-5xl mx-auto space-y-6" data-id="element-4904">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-4905">
          <div data-id="element-4906">
            <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2" data-id="element-4907">
              <Activity className="text-indigo-400" data-id="element-4908" /> Diagnóstico de Conexiones
            </h1>
            <p className="text-slate-400 text-sm mt-1" data-id="element-4909">
              Monitoreá el estado y latencia de todas tus integraciones en
              tiempo real.
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-4910">
            <Button variant="outline" onClick={exportDiagnostics} className="gap-2 text-slate-300" data-id="element-4911">
              <Download size={16} data-id="element-4912" /> Exportar JSON
            </Button>
            <Button onClick={runAllChecks} isLoading={isCheckingAll} className="gap-2 bg-indigo-600 hover:bg-indigo-500" data-id="element-4913">
              <RefreshCw size={16} className={isCheckingAll ? 'animate-spin' : ''} data-id="element-4914" />
              Probar Todo
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-id="element-4915">
          <Card className="bg-slate-900 border-slate-800 p-4" data-id="element-4916">
            <div className="flex items-center gap-3 mb-2" data-id="element-4917">
              <div className="p-2 bg-slate-800 rounded-lg" data-id="element-4918">
                <Activity size={16} className="text-slate-400" data-id="element-4919" />
              </div>
              <p className="text-sm font-medium text-slate-400" data-id="element-4920">
                Total Conectores
              </p>
            </div>
            <p className="text-2xl font-bold text-slate-100" data-id="element-4921">
              {healthData.length}
            </p>
          </Card>
          <Card className="bg-slate-900 border-emerald-900/30 p-4" data-id="element-4922">
            <div className="flex items-center gap-3 mb-2" data-id="element-4923">
              <div className="p-2 bg-emerald-950/50 rounded-lg" data-id="element-4924">
                <CheckCircle2 size={16} className="text-emerald-400" data-id="element-4925" />
              </div>
              <p className="text-sm font-medium text-slate-400" data-id="element-4926">Saludables</p>
            </div>
            <p className="text-2xl font-bold text-emerald-400" data-id="element-4927">
              {connectedCount}
            </p>
          </Card>
          <Card className="bg-slate-900 border-amber-900/30 p-4" data-id="element-4928">
            <div className="flex items-center gap-3 mb-2" data-id="element-4929">
              <div className="p-2 bg-amber-950/50 rounded-lg" data-id="element-4930">
                <AlertTriangle size={16} className="text-amber-400" data-id="element-4931" />
              </div>
              <p className="text-sm font-medium text-slate-400" data-id="element-4932">Degradados</p>
            </div>
            <p className="text-2xl font-bold text-amber-400" data-id="element-4933">{degradedCount}</p>
          </Card>
          <Card className="bg-slate-900 border-red-900/30 p-4" data-id="element-4934">
            <div className="flex items-center gap-3 mb-2" data-id="element-4935">
              <div className="p-2 bg-red-950/50 rounded-lg" data-id="element-4936">
                <XCircle size={16} className="text-red-400" data-id="element-4937" />
              </div>
              <p className="text-sm font-medium text-slate-400" data-id="element-4938">
                Desconectados
              </p>
            </div>
            <p className="text-2xl font-bold text-red-400" data-id="element-4939">
              {disconnectedCount}
            </p>
          </Card>
        </div>

        {/* Connectors List */}
        <Card className="bg-slate-900 border-slate-800 overflow-hidden" data-id="element-4940">
          <div className="overflow-x-auto" data-id="element-4941">
            <table className="w-full text-sm text-left" data-id="element-4942">
              <thead className="text-xs text-slate-400 uppercase bg-slate-950/50 border-b border-slate-800" data-id="element-4943">
                <tr data-id="element-4944">
                  <th className="px-6 py-4 font-medium" data-id="element-4945">Servicio</th>
                  <th className="px-6 py-4 font-medium" data-id="element-4946">Estado</th>
                  <th className="px-6 py-4 font-medium" data-id="element-4947">Latencia</th>
                  <th className="px-6 py-4 font-medium" data-id="element-4948">Última Verificación</th>
                  <th className="px-6 py-4 font-medium" data-id="element-4949">Token Expira</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-4950">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50" data-id="element-4951">
                {healthData.map(connector => {
                const isChecking = connector.status === 'checking';
                const canReport = connector.status === 'disconnected' || connector.status === 'degraded';
                const bugStatus = reportStatus[connector.connectorId] || 'idle';
                return <tr key={connector.connectorId} className="hover:bg-slate-800/20 transition-colors" data-id="element-4952">
                      <td className="px-6 py-4" data-id="element-4953">
                        <div className="flex items-center gap-3" data-id="element-4954">
                          <StatusIcon status={connector.status} isChecking={isChecking} data-id="element-4955" />

                          <span className="font-medium text-slate-200" data-id="element-4956">
                            {connector.name}
                          </span>
                        </div>
                        {connector.lastError && <p className="text-xs text-red-400 mt-1 max-w-xs truncate" title={connector.lastError} data-id="element-4957">
                            {connector.lastError}
                          </p>}
                      </td>
                      <td className="px-6 py-4" data-id="element-4958">
                        <StatusBadge status={connector.status} data-id="element-4959" />
                      </td>
                      <td className="px-6 py-4" data-id="element-4960">
                        {connector.latency !== null ? <span className={`font-mono ${connector.latency > 1000 ? 'text-amber-400' : 'text-slate-300'}`} data-id="element-4961">
                            {connector.latency}ms
                          </span> : <span className="text-slate-500" data-id="element-4962">—</span>}
                      </td>
                      <td className="px-6 py-4 text-slate-400" data-id="element-4963">
                        {formatTimeAgo(connector.lastChecked)}
                      </td>
                      <td className="px-6 py-4" data-id="element-4964">
                        <span className={`text-xs px-2 py-1 rounded bg-slate-950 border border-slate-800 ${connector.tokenExpiresIn !== 'N/A' ? 'text-indigo-300' : 'text-slate-500'}`} data-id="element-4965">
                          {connector.tokenExpiresIn}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-4966">
                        <div className="flex items-center justify-end gap-2" data-id="element-4967">
                          <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-400 hover:text-indigo-400" onClick={() => runCheck(connector.connectorId)} disabled={isChecking} data-id="element-4968">
                            <RefreshCw size={14} className={isChecking ? 'animate-spin' : ''} data-id="element-4969" />
                          </Button>
                          {canReport && <Button variant="ghost" size="sm" className={`h-8 px-2 gap-1.5 text-xs ${bugStatus === 'sent' ? 'text-emerald-400' : bugStatus === 'error' ? 'text-red-400' : 'text-orange-400 hover:text-orange-300'}`} onClick={() => reportBugToJira(connector)} disabled={bugStatus === 'sending' || bugStatus === 'sent'} title="Reportar como bug en Jira" data-id="element-4970">
                              {bugStatus === 'sending' ? <>
                                  <Loader2 size={14} className="animate-spin" data-id="element-4971" />{' '}
                                  Enviando
                                </> : bugStatus === 'sent' ? <>
                                  <Check size={14} data-id="element-4972" /> Reportado
                                </> : bugStatus === 'error' ? <>
                                  <XCircle size={14} data-id="element-4973" /> Error
                                </> : <>
                                  <Bug size={14} data-id="element-4974" /> Reportar
                                </>}
                            </Button>}
                          <Button variant="outline" size="sm" className="h-8 text-xs border-slate-700" onClick={() => onNavigate?.('settings')} data-id="element-4975">
                            Reconectar
                          </Button>
                        </div>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Report Feedback Banner */}
        {reportMessage && <div className={`mt-4 px-4 py-3 rounded-lg border text-sm flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300 ${reportMessage.type === 'success' ? 'bg-emerald-950/40 border-emerald-800/50 text-emerald-300' : 'bg-red-950/40 border-red-800/50 text-red-300'}`} data-id="element-4976">
            {reportMessage.type === 'success' ? <Check size={16} className="text-emerald-400 shrink-0" data-id="element-4977" /> : <XCircle size={16} className="text-red-400 shrink-0" data-id="element-4978" />}
            <span className="flex-1" data-id="element-4979">{reportMessage.text}</span>
            <button onClick={() => setReportMessage(null)} className="text-slate-500 hover:text-slate-300 p-1" data-id="element-4980">
              ×
            </button>
          </div>}
      </div>
    </div>;
}